import { Paper, Group, Title, Button, Box } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useSuspenseQuery, useMutation } from '@tanstack/react-query';
import { useParams, useRouter } from '@tanstack/react-router';
import { multiPolygon, featureCollection, flatten } from '@turf/turf';
import { MultiPolygon, Feature, Polygon } from 'geojson';
import { MapEvent, MapMouseEvent } from 'mapbox-gl';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapRef } from 'react-map-gl';

import { CommittedFieldsTable } from '../../components/committed-fields-table/committed-fields-table';
import { contractsApi } from '../../lib/apis/contracts';
import { useContractsQueries } from '../../lib/queries/contracts';

import { ValidateBatchForm } from './component/validate-batch-form';
import { PageSkeleton } from './page.skeleton';

import { FORM_STATUS } from '@data-access';
import { fieldsApi, useCreateField } from '@fields/data-access';
import {
  zoomOnFeature,
  PfMap,
  StyleControl,
  DrawToolboxControl,
  PlotsLayer,
  PLOTS_LAYER_ID,
} from '@map';
import { FullMainHeight, Drawer } from '@ui';

export const INTERACTIVE_LAYER_IDS = [
  PLOTS_LAYER_ID,
  `${PLOTS_LAYER_ID}_symbol`,
];

export function BatchDetailsPage() {
  const { t } = useTranslation();
  const mapRef = useRef<MapRef>(null);
  const { batchId, contractId } = useParams({
    from: '/_app/os/contracts_/$contractId/batches_/$batchId',
  });
  const { history, navigate } = useRouter();
  const [opened, actions] = useDisclosure(false);
  const [drawMode, setDrawMode] = useState<'edit' | 'create'>('edit');

  const contractsQueries = useContractsQueries();

  const batchDetails = useSuspenseQuery(
    contractsQueries.batchesDetail(Number(batchId)),
  );
  const contractDetails = useSuspenseQuery(
    contractsQueries.detail(Number(contractId)),
  );
  const confirmBatch = useMutation({
    mutationFn: contractsApi.batches.confirmBatch,
  });
  const updateField = useMutation({
    mutationFn: fieldsApi.updateOne,
  });
  const createField = useCreateField();

  const [initialPolygon, setInitialPolygon] = useState<
    GeoJSON.Feature | GeoJSON.FeatureCollection
  >();
  const [newPolygon, setNewPolygon] = useState<GeoJSON.Feature<
    MultiPolygon,
    any
  > | null>(null);
  const [selectedFieldId, setSelectedFieldId] = useState<number | null>(null);

  const handleConfirmBatch = () => {
    confirmBatch.mutate(Number(batchId), {
      onSuccess: () => {
        actions.close();
        handleBack();
      },
    });
  };

  const handleLoad = (map: MapEvent) => {
    zoomOnFeature({
      map: map.target,
      geojson: batchDetails.data.lot_fields,
    });
  };

  const handleMapClick = (e: MapMouseEvent) => {
    if (selectedFieldId || drawMode === 'create') {
      return;
    }
    const features = mapRef.current?.getMap().queryRenderedFeatures(e.point);
    if (features && features.length > 0) {
      const feature = features[0];
      const featureId = feature.id as number | undefined;
      const initialFeature = batchDetails.data.lot_fields.features.find(
        (f) => f.id === featureId,
      );
      if (featureId) {
        setSelectedFieldId(featureId);
        if (initialFeature) {
          const { id, ...f } = initialFeature;
          setInitialPolygon(flatten(f));
          zoomOnFeature({
            map: mapRef.current?.getMap(),
            geojson: initialFeature,
          });
        }
      }
    }
  };

  const handleZoomOnField = (fieldId: number) => {
    const field = batchDetails.data.lot_fields.features.find(
      (f) => f.id === fieldId,
    );
    if (field) {
      zoomOnFeature({
        map: mapRef.current?.getMap(),
        geojson: field,
      });
    }
  };

  const handleDrawEnd = (features: Feature<Polygon>[]) => {
    const newBoundaries = multiPolygon(
      features.map((f) => f.geometry.coordinates),
      {},
    );

    setNewPolygon(newBoundaries);
  };

  const handleUpdateBoundaries = () => {
    if (newPolygon && selectedFieldId) {
      updateField.mutate(
        {
          id: selectedFieldId,
          boundaries: featureCollection([newPolygon]),
        },
        {
          onSuccess: () => {
            handleReset();
          },
          onError: (error) => {
            notifications.show({
              color: 'red',
              message: t('common.update-field-error'),
              title: t('common.ERROR'),
            });
          },
        },
      );
    }
  };

  const handleReset = () => {
    setDrawMode('edit');
    setSelectedFieldId(null);
    setInitialPolygon(featureCollection([]));
    setNewPolygon(null);
  };

  const handleCreateField = () => {
    handleReset();
    setDrawMode('create');
    setInitialPolygon(featureCollection([]));
  };

  const handleSaveField = () => {
    if (newPolygon) {
      createField.mutate(
        {
          boundaries: featureCollection([newPolygon]),
          name: 'Nouvelle parcelle',
          farm: contractDetails.data.farm.id,
          lot: Number(batchId),
        },
        {
          onSuccess: () => {
            handleReset();
          },
        },
      );
    }
  };

  const handleBack = () => {
    if (history.location.state.key) {
      history.back();
    } else {
      navigate({
        to: '/os/contracts/$contractId/batches',
        params: {
          contractId,
        },
      });
    }
  };

  return (
    <FullMainHeight.Root>
      <FullMainHeight.Header>
        <Paper>
          <Group justify="space-between">
            <Title order={2}>
              {t('common.batch')} : {batchDetails.data.name}
            </Title>
            <Group>
              <Button
                disabled={batchDetails.data.status === FORM_STATUS.FINISHED}
                onClick={actions.open}
              >
                {t('common.valider-ma-saisie')}
              </Button>
              <Button color="red" onClick={handleBack}>
                {t('common.back')}
              </Button>
            </Group>
          </Group>
        </Paper>
      </FullMainHeight.Header>
      <FullMainHeight.Content>
        <Group h="100%" wrap="nowrap">
          <Box h="100%">
            <CommittedFieldsTable
              batchId={Number(batchId)}
              onResetSelectedField={handleReset}
              onSelectField={handleZoomOnField}
              selectedFieldId={selectedFieldId}
            />
          </Box>
          <Paper h="100%" p={0} pos="relative" w="100%">
            <PfMap
              interactiveLayerIds={INTERACTIVE_LAYER_IDS}
              onClick={handleMapClick}
              onLoad={handleLoad}
              ref={mapRef}
            >
              <StyleControl />

              <DrawToolboxControl
                disabled={drawMode === 'edit' && !selectedFieldId}
                guidanceControls={['draw', 'edit', 'cut', 'delete_point']}
                initialPolygon={initialPolygon}
                key={drawMode}
                onDrawEnd={handleDrawEnd}
              />

              {!selectedFieldId ? (
                <PlotsLayer data={batchDetails.data.lot_fields} />
              ) : null}
            </PfMap>
            <Paper bottom={10} left={10} pos="absolute">
              {selectedFieldId ? (
                <Group>
                  <Button
                    disabled={!newPolygon}
                    loading={updateField.isPending}
                    onClick={handleUpdateBoundaries}
                  >
                    {t('common.save-field-boundaries')}
                  </Button>
                  <Button color="red" onClick={handleReset}>
                    {t('common.cancel')}
                  </Button>
                </Group>
              ) : drawMode === 'edit' ? (
                <Button onClick={handleCreateField}>
                  {t('common.createPlot')}
                </Button>
              ) : (
                <Group>
                  <Button
                    disabled={!newPolygon}
                    loading={createField.isPending}
                    onClick={handleSaveField}
                  >
                    {t('common.save-field')}
                  </Button>
                  <Button color="red" onClick={handleReset}>
                    {t('common.cancel')}
                  </Button>
                </Group>
              )}
            </Paper>
          </Paper>
        </Group>

        <Drawer
          onClose={actions.close}
          opened={opened}
          position="right"
          title={t('common.valider-ma-saisie')}
        >
          <ValidateBatchForm
            area={batchDetails.data.area}
            campaign={batchDetails.data.campaign.name}
            error={confirmBatch.isError}
            lot_engaged_fields_stats={
              batchDetails.data.lot_engaged_fields_stats
            }
            name={batchDetails.data.name}
            onCancel={actions.close}
            onConfirmBatch={handleConfirmBatch}
            tonnage={batchDetails.data.tonnage}
          />
        </Drawer>
      </FullMainHeight.Content>
    </FullMainHeight.Root>
  );
}

BatchDetailsPage.Skeleton = PageSkeleton;
